import {
	Table,
	Text,
	Inline,
	Badge,
	Icons,
	Stack,
	RelativeTime,
	Button,
	Dropdown,
	Box,
	Alert,
	Divider,
} from "@sembark-travel/ui-base"
import {
	areAdvancedFiltersAppliedDefault,
	ListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import {
	ConfirmationDialog,
	ConfirmationDialogProvider,
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	useDialog,
} from "@sembark-travel/ui-dialog"
import { TRateSheetDetail, TRateSheetShare, rateSheetsXHR } from "./store"
import { EditRateSheetNameInDialog } from "./NewItem"
import { Link, useLocationQuery } from "@sembark-travel/ui-router"
import {
	SelectField,
	Form,
	validateFormValues,
	withServerErrors,
	SubmissionError,
	SwitchInputField,
	useFieldValue,
} from "@sembark-travel/ui-form"
import * as Validator from "yup"
import { generatePath, useLocation, useNavigate } from "../router-utils"
import { ReactNode, useEffect, useMemo, useState } from "react"
import { IHotel, SelectHotels } from "../Hotels"
import { ITripSource, SelectTripSources } from "../TripSources"
import { useXHR } from "@sembark-travel/xhr"
import { showSnackbar } from "@sembark-travel/ui-snackbar"

type TFilters = TSearchParams & {
	hotels?: Array<IHotel>
	trip_sources?: Array<ITripSource>
}
type TFiltersInQuery = TSearchParams & {
	hotels?: string[]
	trip_sources?: string[]
}

// Filters travel in the url as `${id}_${name}`.
function parseIdAndName(value: string) {
	const slices = value.split("_")
	return { id: parseInt(slices[0]), name: slices.slice(1).join("_") }
}

function filtersToQuery(filters: TFilters): TFiltersInQuery {
	const { hotels, trip_sources, ...others } = filters
	const query: TFiltersInQuery = { ...others }

	if (hotels && hotels.length)
		query.hotels = hotels.map((t) => `${t.id}_${t.name}`)
	if (trip_sources && trip_sources.length)
		query.trip_sources = trip_sources.map((t) => `${t.id}_${t.name}`)
	return query
}

function queryToFilters(query: TFiltersInQuery): TFilters {
	const { hotels, trip_sources, ...others } = query
	const filters: TFilters = { ...others }

	if (hotels && hotels.length) {
		filters.hotels = hotels.map(parseIdAndName) as unknown as Array<IHotel>
	}
	if (trip_sources && trip_sources.length) {
		filters.trip_sources = trip_sources.map(
			parseIdAndName
		) as unknown as Array<ITripSource>
	}
	return filters
}

function filtersToRequestParams(filters: TFilters) {
	const { hotels, trip_sources, ...otherParams } = filters
	return {
		...otherParams,
		hotels: (hotels || []).map((h) => h.id),
		trip_sources: (trip_sources || []).map((t) => t.id),
	}
}

// Set by the edit form when the saved rate sheet is not applied to all DMCs
type TRateSheetsLocationState = { shareRateSheetId?: number } | null | undefined

function RateSheetsList() {
	const location = useLocation()
	const navigate = useNavigate()
	const [query, setQuery] = useLocationQuery({
		toQuery: filtersToQuery,
		fromQuery: queryToFilters,
	})
	const [params, setParams] = useSearch<TFilters>(query)

	useEffect(() => {
		setQuery(params)
	}, [setQuery, params])

	const shareRateSheetIdFromState = (location.state as TRateSheetsLocationState)
		?.shareRateSheetId
	const [shareRateSheetId, setShareRateSheetId] = useState(
		shareRateSheetIdFromState
	)

	// so that a refresh or going back doesn't open the dialog again
	useEffect(() => {
		if (!shareRateSheetIdFromState) return
		navigate(
			{ pathname: "/org/rate-sheets", search: location.search },
			{ replace: true }
		)
	}, [shareRateSheetIdFromState, navigate, location.search])

	return (
		<Search
			title="B2B Rate Sheets"
			placeholder="Search by Name..."
			initialParams={params}
			onSearch={(params) => {
				setParams({ ...params, page: 1 })
			}}
			Filters={Filters}
			resetParams={{
				q: "",
				page: 1,
			}}
			areAdvancedFiltersApplied={areAdvancedFiltersAppliedDefault}
			actions={<AddRateSheet />}
		>
			<ListView<TRateSheetDetail, TFilters>
				pageKey="rate-sheets"
				params={params}
				fetch={(xhr, params) => {
					return rateSheetsXHR(xhr).get({
						...filtersToRequestParams(params),
						include:
							"shared_with_trip_sources_count,shared_with_trip_sources_behind_count",
					})
				}}
				onPageChange={(page) => setParams({ ...params, page })}
			>
				{({ items, refresh }) => (
					<Table
						responsive
						bordered
						headers={[
							"Rate Sheet Name",
							"Applicable on",
							"Hotels",
							"Seasons",
							// Commenting because backend is not providing these values yet
							// "Promotions",
							// "Restrictions",
							"Last Updated by",
							"",
						]}
						rows={items.map((item) => {
							const sheet = item.latest_rate_sheet
							const sharedWithCount = item.shared_with_trip_sources_count || 0
							const behindCount =
								item.shared_with_trip_sources_behind_count || 0
							// const promotionsCount = item.promotions_count ?? 0
							// const restrictionsCount = item.restrictions_count ?? 0
							// const warningsCount = item.warnings_count ?? 0
							// const isStale = item.is_stale ?? false
							return [
								<Stack gap="1">
									<Inline gap="2" alignItems="center" flexWrap="wrap">
										<Link
											to={generatePath("/rate-sheets/:rateSheetId", {
												rateSheetId: String(item.id),
											})}
											anchored
											color="accent"
											fontWeight="semibold"
										>
											{item.name}
										</Link>
										<Badge size="sm" primary borderWidth="0">
											code: {item.id}
										</Badge>
										<Badge size="sm">v{sheet.version}</Badge>
									</Inline>
									{/* {warningsCount || isStale ? (
										<Inline gap="2" alignItems="center" flexWrap="wrap">
											{warningsCount ? (
												<Badge size="sm" status="warning">
													<Inline as="span" gap="1" alignItems="center">
														<Icons.AttentionSolid size="3" />
														{warningsCount} warning
														{warningsCount > 1 ? "s" : ""}
													</Inline>
												</Badge>
											) : null}
											{isStale ? (
												<Badge size="sm" status="danger">
													<Inline as="span" gap="1" alignItems="center">
														<Icons.CircleAlert size="3" />
														Stale
													</Inline>
												</Badge>
											) : null}
										</Inline>
									) : null} */}
								</Stack>,
								item.applies_to_all_trip_sources ? (
									<Badge size="sm" status="success" borderWidth="0">
										All B2B Sources
									</Badge>
								) : sharedWithCount ? (
									<Inline gap="2" alignItems="center" flexWrap="wrap">
										<Text>{sharedWithCount}</Text>
										{behindCount ? (
											<BehindBadge
												size="sm"
												title="Partners still on an older version"
											>
												{behindCount} Behind
											</BehindBadge>
										) : null}
									</Inline>
								) : (
									<Text color="muted">0</Text>
								),
								<Text>{sheet.hotels_count ?? sheet.hotels.length}</Text>,
								<Text>{sheet.seasons_count ?? sheet.seasons.length}</Text>,
								// Commenting because backend is not providing these values yet
								// <CountWithIcon
								// 	count={promotionsCount}
								// 	icon={<Icons.BadgePercentage size="4" color="success" />}
								// />,
								// <CountWithIcon
								// 	count={restrictionsCount}
								// 	icon={<Icons.ShieldBan size="4" color="warning" />}
								// />,
								<Stack justifyContent="start">
									<Text>{item.latest_rate_sheet.created_by.name || "-"}</Text>
									<Text color="muted" fontSize="sm">
										<RelativeTime
											timestamp={item.latest_rate_sheet.created_at}
										/>
									</Text>
								</Stack>,
								<Inline gap="2" alignItems="center" justifyContent="end">
									<ShareRateSheetInDialog
										rateSheet={item}
										onChange={refresh}
										defaultOpen={item.id === shareRateSheetId}
										onClose={() => setShareRateSheetId(undefined)}
									>
										{({ open }) => (
											<Button
												size="sm"
												status="primary"
												level="primary"
												onClick={open}
											>
												<Icons.Share />
												Share
											</Button>
										)}
									</ShareRateSheetInDialog>
								</Inline>,
							]
						})}
					/>
				)}
			</ListView>
		</Search>
	)
}

// function CountWithIcon({ count, icon }: { count: number; icon: ReactNode }) {
// 	if (!count) {
// 		return <Text color="muted">-</Text>
// 	}
// 	return (
// 		<Inline gap="1" alignItems="center" justifyContent="center">
// 			{icon}
// 			<Text fontWeight="semibold">{count}</Text>
// 		</Inline>
// 	)
// }

function AddRateSheet() {
	const navigate = useNavigate()

	return (
		<>
			<EditRateSheetNameInDialog
				placeholder="e.g. North East - 3 Star Hotels - Summer 2026"
				onSuccess={(name) =>
					navigate("/rate-sheets/new", {
						state: { initialValues: { name } },
					})
				}
			>
				{({ open }) => (
					<Button onClick={open} status="primary" level="primary">
						Create Rate Sheet
					</Button>
				)}
			</EditRateSheetNameInDialog>
		</>
	)
}

function Filters() {
	return (
		<Stack gap="4">
			<SelectField
				select={SelectHotels}
				label="Hotels"
				name="hotels"
				multiple
				fetchOnMount
			/>
			<SelectField
				select={SelectTripSources}
				label="B2B Sources"
				name="trip_sources"
				multiple
				isAgent={1}
				fetchOnMount
			/>
		</Stack>
	)
}

export { RateSheetsList }

function showErrorSnackbar(e: unknown) {
	showSnackbar(
		(e as Error).message || "Something went wrong. Please try again."
	)
}

function BehindBadge({
	children,
	size,
	title,
}: {
	children: ReactNode
	size?: "sm"
	title?: string
}) {
	return (
		<Badge size={size} status="danger" borderWidth="0" title={title}>
			<Inline as="span" gap="1" alignItems="center">
				<Icons.CircleAlert size="3" />
				{children}
			</Inline>
		</Badge>
	)
}

function RateSheetShareList({
	shares,
	renderAction,
}: {
	shares: TRateSheetShare[]
	renderAction?: (share: TRateSheetShare) => ReactNode
}) {
	return (
		<Box borderWidth="1" borderColor="emphasis" rounded="md">
			{shares.map((share, index) => (
				<Box
					key={share.id}
					borderBottomWidth={index === shares.length - 1 ? "0" : "1"}
					paddingX="4"
					paddingY="3"
				>
					<Inline
						gap="2"
						alignItems="center"
						justifyContent="between"
						flexWrap="wrap"
					>
						<Inline gap="2" alignItems="center" flexWrap="wrap">
							<Text fontWeight="semibold">{share.trip_source.name}</Text>
							<Badge size="sm">v{share.version}</Badge>
						</Inline>
						<Inline gap="2" alignItems="center">
							{share.versions_behind > 0 ? (
								<BehindBadge>
									{share.versions_behind} version
									{share.versions_behind > 1 ? "s" : ""} behind
								</BehindBadge>
							) : null}
							{renderAction?.(share)}
						</Inline>
					</Inline>
				</Box>
			))}
		</Box>
	)
}

function ShareRateSheetInDialog({
	rateSheet,
	onChange,
	defaultOpen = false,
	onClose,
	children,
}: {
	rateSheet: TRateSheetDetail
	// called once the shares change, to refresh the rate sheets table
	onChange: () => void
	defaultOpen?: boolean
	onClose?: () => void
	children: (props: { open: () => void }) => ReactNode
}) {
	const [isOpen, open, closeDialog] = useDialog(defaultOpen)
	function close() {
		closeDialog()
		onClose?.()
	}
	return (
		<>
			{children({ open })}
			<Dialog
				open={isOpen}
				onClose={close}
				title={
					<Inline gap="2" alignItems="center">
						<Text>Share - {rateSheet.name}</Text>
						<Badge size="sm">v{rateSheet.latest_rate_sheet.version}</Badge>
					</Inline>
				}
				sm
			>
				<DialogBody>
					<ShareRateSheet
						rateSheet={rateSheet}
						onChange={onChange}
						onAppliesToAllChange={() => {
							close()
							onChange()
						}}
					/>
				</DialogBody>
			</Dialog>
		</>
	)
}

// The switch holds 0 | 1, which is what `SwitchInput` reports
type TShareRateSheetFormSchema = {
	applies_to_all_trip_sources: 0 | 1
	trip_sources: ITripSource[]
}

const shareRateSheetValidation = Validator.object().shape({
	trip_sources: Validator.array().min(
		1,
		"Please select at least one B2B source to share with"
	),
})

function ShareRateSheet({
	rateSheet,
	onChange,
	onAppliesToAllChange,
}: {
	rateSheet: TRateSheetDetail
	onChange: () => void
	// called once the sheet has been applied to all the DMCs
	onAppliesToAllChange: () => void
}) {
	const [freshKey, setFreshKey] = useState(1)

	function handleChange() {
		setFreshKey((prev) => prev + 1)
		onChange()
	}

	return (
		<ShareRateSheetForm
			rateSheet={rateSheet}
			onSuccess={handleChange}
			onAppliesToAllChange={onAppliesToAllChange}
		>
			<RateSheetShares
				rateSheet={rateSheet}
				freshKey={freshKey}
				onChange={handleChange}
			/>
		</ShareRateSheetForm>
	)
}

function ShareRateSheetForm({
	rateSheet,
	onSuccess,
	onAppliesToAllChange,
	children,
}: {
	rateSheet: TRateSheetDetail
	onSuccess: () => void
	onAppliesToAllChange: () => void
	// rendered below the form, inside it, so it can follow the form's switch
	children?: ReactNode
}) {
	const xhr = useXHR()
	const isAppliedToAll = rateSheet.applies_to_all_trip_sources
	const initialValues = useMemo<TShareRateSheetFormSchema>(
		() => ({
			applies_to_all_trip_sources: isAppliedToAll ? 1 : 0,
			trip_sources: [],
		}),
		[isAppliedToAll]
	)

	return (
		<Form<TShareRateSheetFormSchema>
			initialValues={initialValues}
			validate={validateFormValues(shareRateSheetValidation)}
			onSubmit={withServerErrors(async ({ trip_sources }, form) => {
				await rateSheetsXHR(xhr).shareTripSources(rateSheet.id, {
					applies_to_all_trip_sources: 0,
					trip_sources: trip_sources.map((tripSource) => tripSource.id),
				})
				showSnackbar(
					`Rate sheet shared with ${trip_sources.length} B2B source${trip_sources.length > 1 ? "s" : ""}.`
				)
				form.restart()
				onSuccess()
			})}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting }) => (
				<Stack gap="4">
					<form noValidate onSubmit={handleSubmit}>
						<ShareRateSheetFields
							rateSheet={rateSheet}
							submitting={submitting}
							onAppliesToAllChange={onAppliesToAllChange}
						/>
					</form>
					{children}
				</Stack>
			)}
		</Form>
	)
}

function ShareRateSheetFields({
	rateSheet,
	submitting,
	onAppliesToAllChange,
}: {
	rateSheet: TRateSheetDetail
	submitting: boolean
	// called once the sheet has been applied to, or taken off, all the DMCs
	onAppliesToAllChange: () => void
}) {
	const isAppliedToAll = rateSheet.applies_to_all_trip_sources
	const { value: isApplyingToAll } = useFieldValue<
		TShareRateSheetFormSchema["applies_to_all_trip_sources"]
	>("applies_to_all_trip_sources")
	const { value: tripSources, onChange: setTripSources } =
		useFieldValue<TShareRateSheetFormSchema["trip_sources"]>("trip_sources")
	const canShare = tripSources.length > 0

	return (
		<Stack gap="3">
			<AppliesToAllDmcsSwitch
				rateSheet={rateSheet}
				onSuccess={onAppliesToAllChange}
			/>
			<Divider marginY="0" />
			{isApplyingToAll ? (
				<Stack gap="3">
					<Alert status="info">
						<Text as="span" fontWeight="semibold">
							{rateSheet.name}
						</Text>{" "}
						{isAppliedToAll ? "is" : "will be"} applied to all B2B Sources.
					</Alert>
					{isAppliedToAll ? null : (
						<Inline justifyContent="end">
							<ApplyToAllDmcsButton
								rateSheet={rateSheet}
								onSuccess={onAppliesToAllChange}
							/>
						</Inline>
					)}
				</Stack>
			) : (
				<Stack gap="1">
					<Text fontWeight="semibold">Search B2B Sources to share</Text>
					<Inline gap="4" alignItems="start" flexWrap="wrap">
						<Box flex="1" maxWidth="sm">
							<SelectField
								select={SelectTripSources}
								name="trip_sources"
								placeholder="Type to search..."
								multiple
								creatable
								isAgent={1}
								notSharedWithRateSheetHead={rateSheet.id}
								onCreateSuccess={(newTripSource: ITripSource) => {
									setTripSources([...(tripSources || []), newTripSource])
								}}
							/>
						</Box>
						<Button
							type="submit"
							status="primary"
							level="primary"
							disabled={submitting || !canShare}
							style={{ opacity: canShare ? 1 : 0.5 }}
						>
							{submitting ? <Icons.Spinner /> : <Icons.Share />}
							Share
						</Button>
					</Inline>
				</Stack>
			)}
			<SubmissionError />
		</Stack>
	)
}

function AppliesToAllDmcsSwitch({
	rateSheet,
	onSuccess,
}: {
	rateSheet: TRateSheetDetail
	onSuccess: () => void
}) {
	const xhr = useXHR()
	const [isUpdating, setIsUpdating] = useState(false)
	const { onChange: setIsApplyingToAll } = useFieldValue<
		TShareRateSheetFormSchema["applies_to_all_trip_sources"]
	>("applies_to_all_trip_sources")

	return (
		<ConfirmationDialogProvider>
			{({ confirm }) => (
				<>
					<SwitchInputField
						name="applies_to_all_trip_sources"
						label="Apply this rate sheet to all B2B Sources"
						disabled={isUpdating}
						onChange={async (isChecked) => {
							// the switch only moves once the sheet is saved as such,
							// taking it off is a change to the sheet, so it is confirmed
							if (isChecked || !rateSheet.applies_to_all_trip_sources) {
								setIsApplyingToAll(isChecked)
								return
							}
							if (!(await confirm())) return
							setIsUpdating(true)
							try {
								await rateSheetsXHR(xhr).shareTripSources(rateSheet.id, {
									applies_to_all_trip_sources: 0,
									trip_sources: [],
									upgrade_existing_trip_sources_to_latest_version: 1,
								})
								showSnackbar(
									`${rateSheet.name} is no longer applied to all B2B Sources.`
								)
								onSuccess()
							} catch (e) {
								showErrorSnackbar(e)
							} finally {
								setIsUpdating(false)
							}
						}}
					/>
					<ConfirmationDialog
						title="Remove from all B2B Sources"
						acceptActionLabel="Yes, remove"
					>
						<Alert status="warning">
							Are you sure you want to stop applying{" "}
							<Text as="span" fontWeight="semibold">
								{rateSheet.name}
							</Text>{" "}
							to all B2B Sources? B2B Sources it was shared with individually
							keep access and are moved to the latest version.
						</Alert>
					</ConfirmationDialog>
				</>
			)}
		</ConfirmationDialogProvider>
	)
}

function ApplyToAllDmcsButton({
	rateSheet,
	onSuccess,
}: {
	rateSheet: TRateSheetDetail
	onSuccess: () => void
}) {
	const xhr = useXHR()
	const [isApplying, setIsApplying] = useState(false)

	async function onApply() {
		if (isApplying) return
		setIsApplying(true)
		try {
			await rateSheetsXHR(xhr).shareTripSources(rateSheet.id, {
				applies_to_all_trip_sources: 1,
			})
			showSnackbar(`${rateSheet.name} will be applied to all B2B Sources.`)
			onSuccess()
		} catch (e) {
			showErrorSnackbar(e)
		} finally {
			setIsApplying(false)
		}
	}

	return (
		<Button
			type="button"
			status="primary"
			level="primary"
			onClick={onApply}
			disabled={isApplying}
		>
			{isApplying ? <Icons.Spinner /> : <Icons.Ok />}
			Apply
		</Button>
	)
}

function RateSheetShares({
	rateSheet,
	freshKey,
	onChange,
}: {
	rateSheet: TRateSheetDetail
	freshKey: number
	onChange: () => void
}) {
	const [params, setParams] = useSearch<TSearchParams>({ q: "", limit: 15 })
	const sharedCount = rateSheet.shared_with_trip_sources_count || 0
	const behindCount = rateSheet.shared_with_trip_sources_behind_count || 0
	// lives inside the share form, so the list follows its switch
	const { value: isApplyingToAll } = useFieldValue<
		TShareRateSheetFormSchema["applies_to_all_trip_sources"]
	>("applies_to_all_trip_sources")

	if (isApplyingToAll) return null

	return (
		<Stack>
			<Text fontWeight="semibold">Shared with ({sharedCount})</Text>
			<Search
				placeholder="Type to search..."
				initialParams={params}
				onSearch={(searchParams) => setParams({ ...searchParams, page: 1 })}
				removeContainerPadding
				fullWidthInput
			>
				<Box style={{ maxHeight: "50dvh" }} overflowY="auto">
					<ListView<TRateSheetShare, TSearchParams>
						pageKey={`rate-sheet-trip-sources-${rateSheet.id}-${freshKey}`}
						params={params}
						fetch={(xhr, params) =>
							rateSheetsXHR(xhr).getTripSourcesForRateSheet(
								rateSheet.id,
								params
							)
						}
						onPageChange={(page) => setParams({ ...params, page })}
						removeContainerPadding
						emptyState={({ searched }) => (
							<Stack
								gap="4"
								padding="4"
								alignItems="center"
								justifyContent="center"
							>
								<Icons.Info size="10" color="subtle" />
								<Text fontSize="sm" fontWeight="semibold" color="muted">
									{searched
										? "No B2B Sources matched your search."
										: "No B2B Sources shared yet!"}
								</Text>
							</Stack>
						)}
					>
						{({ items }) => (
							<Stack gap="3">
								<Inline alignItems="center" justifyContent="end" gap="2">
									{behindCount > 0 ? (
										<>
											<BehindBadge>
												{behindCount} B2B Source{behindCount > 1 ? "s" : ""}{" "}
												behind
											</BehindBadge>
											<UpdateBehindDmcsActionInDialog
												headId={rateSheet.id}
												behindShares={items.filter(
													(share) => share.versions_behind > 0
												)}
												behindCount={behindCount}
												onChange={onChange}
											>
												{({ open }) => (
													<Button level="secondary" size="sm" onClick={open}>
														<Icons.Ok />
														Update all behind
													</Button>
												)}
											</UpdateBehindDmcsActionInDialog>
										</>
									) : (
										<Badge status="success">
											All shared B2B Sources up-to-date
										</Badge>
									)}
								</Inline>
								<RateSheetShareList
									shares={items}
									renderAction={(share) => (
										<Dropdown alignRight>
											<Dropdown.ToggleButton level="tertiary" size="sm">
												<Icons.DotsVertical />
											</Dropdown.ToggleButton>
											<Dropdown.Menu>
												<RevokeSharedRateSheetAccessAction
													headId={rateSheet.id}
													rateSheetName={rateSheet.name}
													share={share}
													onChange={onChange}
												/>
											</Dropdown.Menu>
										</Dropdown>
									)}
								/>
							</Stack>
						)}
					</ListView>
				</Box>
			</Search>
		</Stack>
	)
}

function UpdateBehindDmcsActionInDialog({
	headId,
	behindShares,
	behindCount,
	onChange,
	children,
}: {
	headId: number
	// only the shares on the page being viewed, `behindCount` covers all of them
	behindShares: TRateSheetShare[]
	behindCount: number
	onChange: () => void
	children: (props: { open: () => void }) => ReactNode
}) {
	const xhr = useXHR()
	const [isOpen, open, close] = useDialog()
	const [isUpdating, setIsUpdating] = useState(false)
	const hiddenCount = behindCount - behindShares.length

	async function onUpdate() {
		if (isUpdating) return
		setIsUpdating(true)
		try {
			await rateSheetsXHR(xhr).updateTripSourcesToLatest(headId)
			showSnackbar("B2B Sources updated to the latest version.")
			close()
			onChange()
		} catch (e) {
			showErrorSnackbar(e)
		} finally {
			setIsUpdating(false)
		}
	}

	return (
		<>
			{children({ open })}
			<Dialog open={isOpen} onClose={close} sm>
				<DialogHeader title="Update B2B Sources" onClose={close} />
				<DialogBody>
					<Stack gap="4">
						<Alert status="warning">
							{behindCount > 1 ? "All the B2B Sources" : "The B2B Source"}{" "}
							listed below will be updated to the Latest Version of the Rate
							Sheet.
						</Alert>
						<RateSheetShareList shares={behindShares} />
						{hiddenCount > 0 ? (
							<Text color="muted" fontSize="sm">
								and {hiddenCount} more
							</Text>
						) : null}
					</Stack>
				</DialogBody>
				<DialogFooter>
					<Inline justifyContent="end">
						<Button
							status="primary"
							level="primary"
							onClick={onUpdate}
							disabled={isUpdating}
						>
							{isUpdating ? <Icons.Spinner /> : null}
							Update
						</Button>
					</Inline>
				</DialogFooter>
			</Dialog>
		</>
	)
}

function RevokeSharedRateSheetAccessAction({
	headId,
	rateSheetName,
	share,
	onChange,
}: {
	headId: number
	rateSheetName: string
	share: TRateSheetShare
	onChange: () => void
}) {
	const xhr = useXHR()
	const [isRevoking, setIsRevoking] = useState(false)

	return (
		<ConfirmationDialogProvider>
			{({ confirm }) => (
				<>
					<Dropdown.MenuItem
						color="danger"
						onClick={async () => {
							if (isRevoking) return
							if (await confirm()) {
								setIsRevoking(true)
								try {
									const { message } = await rateSheetsXHR(
										xhr
									).revokeTripSourceAccess(headId, share.trip_source.id)
									showSnackbar(message)
									onChange()
								} catch (e) {
									showErrorSnackbar(e)
								} finally {
									setIsRevoking(false)
								}
							}
						}}
					>
						{isRevoking ? <Icons.Spinner /> : <Icons.Ban />} Revoke Access
					</Dropdown.MenuItem>
					<ConfirmationDialog
						title="Revoke Access"
						destructiveAction
						acceptActionLabel="Yes, Revoke"
					>
						<Alert status="error">
							Are you sure you want to revoke access of{" "}
							<Text as="span" fontWeight="semibold">
								{share.trip_source.name}
							</Text>{" "}
							from{" "}
							<Text as="span" fontWeight="semibold">
								{rateSheetName}
							</Text>
							?
						</Alert>
					</ConfirmationDialog>
				</>
			)}
		</ConfirmationDialogProvider>
	)
}
